import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TAppThunk, TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import { HubFeature } from 'types/hubFeatures.type';
import type { IDefaultReducerState } from 'types/state.interface';
import { DrawerPayloadAction } from 'types/utils.type';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from 'utils/sliceHelpers';

type FeatureKey = {
  id: string;
  defaultName: string;
};

const FEATURE_KEYS: FeatureKey[] = [
  { id: 'scheduleviewer', defaultName: 'View schedule' },
  { id: 'scheduleprefs', defaultName: 'Schedule preferences' },
  { id: 'registeration', defaultName: 'Register to groups' },
  { id: 'examupcoming', defaultName: 'Upcoming Exam' },
  { id: 'examavailability', defaultName: 'Availability' },
  { id: 'examinvitations', defaultName: 'Invitation' },
];

export const hubFeatureName = (id: string | undefined) =>
  FEATURE_KEYS.find((item) => item.id === id)?.defaultName || '-';

export type THubFeaturesState = IDefaultReducerState & {
  data: HubFeature[];
  selected: HubFeature | null;
};

export const initialState: THubFeaturesState = {
  loading: false,
  hasErrors: false,
  data: [],
  selected: null,
};

const slice = createSlice({
  name: 'hubFeatures',
  initialState,
  reducers: {
    fetchHubFeaturesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchHubFeaturesSuccess: (
      state,
      { payload }: PayloadAction<HubFeature[]>
    ) => {
      const result: HubFeature[] = FEATURE_KEYS.map((item) => {
        const found = payload.find((data) => data?.id === item.id);
        if (found) {
          return found;
        }
        return {
          id: item.id,
          name: item.defaultName,
          enabled: false,
        };
      });
      state.data = result;
      finishedLoadingSuccess(state);
    },
    fetchHubFeaturesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createHubFeaturesRequest: (state) => {
      isLoadingRequest(state);
    },
    createHubFeaturesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateHubFeaturesRequest: (state) => {
      isLoadingRequest(state);
    },
    updateHubFeature: (state, { payload }: PayloadAction<HubFeature>) => {
      if (!payload) {
        console.log('updateHubFeature: No payload!');
        return;
      }
      const newData = state.data.filter((item) => item.id !== payload?.id);
      newData.push(payload);
      state.data = newData;
    },
    updateHubFeatureValue: <Key extends keyof HubFeature>(
      state: THubFeaturesState,
      { payload }: DrawerPayloadAction<HubFeature, Key>
    ) => {
      const { key, value } = payload;
      state.selected = {
        ...state.selected,
        [key]: value,
      };
    },
    hubFeatureDiscardChanges: (
      state,
      { payload }: PayloadAction<{ id: string }>
    ) => {
      state.selected = state.data.find((item) => item.id === payload.id);
    },
    setSelectedFeature: (state, { payload }: PayloadAction<string>) => {
      state.selected = state.data.find((dashboard) => dashboard.id === payload);
    },
  },
});

export const {
  fetchHubFeaturesRequest,
  fetchHubFeaturesSuccess,
  fetchHubFeaturesFailure,
  createHubFeaturesRequest,
  createHubFeaturesFailure,
  updateHubFeaturesRequest,
  updateHubFeature,
  updateHubFeatureValue,
  hubFeatureDiscardChanges,
  setSelectedFeature,
} = slice.actions;

export const hubFeaturesSelector = (state: TRootState) =>
  state.hubFeatures.data;

export const hubFeatureLoadingSelector = (state: TRootState) =>
  state.hubFeatures.loading;

export const selectedHubFeaturesSelector = (state: TRootState) =>
  state.hubFeatures.selected;

export const fetchHubFeatures = createAppAsyncThunk(
  'hubFeatures/fetchHubFeatures',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchHubFeaturesRequest());
      // TODO call backend
      // const result = await api.get({
      //  endpoint: `/hub/features`,
      //});
      dispatch(fetchHubFeaturesSuccess([]));
    } catch (e) {
      dispatch(fetchHubFeaturesFailure());
      console.error(e);
    }
  }
);

export const checkDisabled = (): TAppThunk<boolean> => (dispatch, getState) => {
  try {
    const { hubFeatures } = getState();
    return checkSavedDisabled(
      hubFeatures.selected,
      hubFeatures.data.find((f) => f.id === hubFeatures.selected?.id)
    );
  } catch (e) {
    console.error(e);
    return false;
  }
};

export default slice.reducer;
